import { useState } from "react";

import {
  useCreateDepartment,
  useDepartments,
  useSetDepartmentArchived,
  useUpdateDepartment
} from "src/api/hooks";
import { ReadFailure } from "src/components/read-failure";
import { Badge, Button, Card, Empty, Field, Input, Select, Table } from "src/components/ui";
import type { DepartmentFieldsFragment } from "src/graphql/generated";
import type { Viewer } from "src/screens/types";

export function Departments({ viewer }: { viewer: Viewer }) {
  const { data, error, isPending } = useDepartments();
  const create = useCreateDepartment();
  const [name, setName] = useState("");
  const [filter, setFilter] = useState("active");
  const mayManage = viewer.roles.includes("IAM_ADMIN");
  const departments = (data?.departments ?? []).filter(
    (department) => filter === "all" || Boolean(department.archivedAt) === (filter === "archived")
  );

  return (
    <div className="space-y-4">
      {mayManage ? (
        <Card title="Add a department">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim() && !create.isPending) {
                create.mutate({ name: name.trim() }, { onSuccess: () => setName("") });
              }
            }}
          >
            <Field label="Department name" required>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={create.isPending}
              />
            </Field>
            <Button type="submit" disabled={!name.trim() || create.isPending}>
              {create.isPending ? "Adding…" : "Add department"}
            </Button>
          </form>
        </Card>
      ) : (
        <Card title="Managing departments">
          <p className="text-sm text-slate-600">
            You can view departments. Adding, renaming, archiving, and restoring them requires
            iam-admin access. Ask an administrator if maintaining this list is part of your work.
          </p>
        </Card>
      )}
      <Card title="Departments">
        <p className="mb-3 text-sm text-slate-600">
          Assign departments to engagements. Archiving removes a department from new assignments and
          preserves existing assignments.
        </p>
        <div className="mb-3 max-w-xs">
          <Field label="Show departments">
            <Select value={filter} onChange={(event) => setFilter(event.target.value)}>
              <option value="active">In use</option>
              <option value="archived">Archived</option>
              <option value="all">All</option>
            </Select>
          </Field>
        </div>
        {error ? (
          <ReadFailure error={error} />
        ) : isPending ? (
          <Empty>Loading…</Empty>
        ) : departments.length === 0 ? (
          <Empty>No departments in this view.</Empty>
        ) : (
          <Table head={mayManage ? ["Name", "Status", "Actions"] : ["Name", "Status"]}>
            {departments.map((department) => (
              <DepartmentRow key={department.id} department={department} mayManage={mayManage} />
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

function DepartmentRow({
  department,
  mayManage
}: {
  department: DepartmentFieldsFragment;
  mayManage: boolean;
}) {
  const update = useUpdateDepartment();
  const archive = useSetDepartmentArchived();
  const [draft, setDraft] = useState<string | null>(null);
  const pending = update.isPending || archive.isPending;
  const archived = Boolean(department.archivedAt);

  return (
    <tr className="border-b border-slate-100">
      <td className="px-2 py-2">
        {draft === null ? (
          department.name
        ) : (
          <Input
            aria-label={`Rename ${department.name}`}
            value={draft}
            disabled={pending}
            onChange={(event) => setDraft(event.target.value)}
          />
        )}
      </td>
      <td className="px-2 py-2">
        <Badge tone={archived ? "amber" : "slate"}>{archived ? "Archived" : "In use"}</Badge>
      </td>
      {mayManage ? (
        <td className="px-2 py-2">
          <div className="flex flex-wrap gap-2">
            {draft === null ? (
              <>
                <Button
                  variant="secondary"
                  disabled={pending}
                  onClick={() => setDraft(department.name)}
                >
                  Rename
                </Button>
                <Button
                  variant="secondary"
                  disabled={pending}
                  onClick={() => archive.mutate({ id: department.id, archived: !archived })}
                >
                  {archived ? "Restore" : "Archive"}
                </Button>
              </>
            ) : (
              <>
                <Button
                  disabled={pending || !draft.trim() || draft.trim() === department.name}
                  onClick={() =>
                    update.mutate(
                      { id: department.id, name: draft.trim() },
                      { onSuccess: () => setDraft(null) }
                    )
                  }
                >
                  Save
                </Button>
                <Button variant="secondary" disabled={pending} onClick={() => setDraft(null)}>
                  Cancel
                </Button>
              </>
            )}
          </div>
        </td>
      ) : null}
    </tr>
  );
}
